/**
 * EmptyState — état vide cohérent (L13.15).
 *
 * Remplace les « Aucun… » bricolés au cas par cas par un composant unique :
 * icône dans une pastille teintée + titre + description + action optionnelle.
 *
 * Deux registres :
 *   - vide « normal »   : aucune donnée encore (liste neuve) → ton neutre/tech
 *   - vide « filtré »   : des données existent mais les filtres ne renvoient rien
 *   - vide « erreur »   : variante `tone="error"` pour les échecs de chargement
 *
 * Le `tone` ne change que la couleur de la pastille d'icône (pas de fond
 * criard) — on reste sobre, c'est un état, pas une alerte.
 *
 * Accessibilité : titre en <p> sémantique simple (pas de heading pour ne pas
 * polluer la hiérarchie de la page) ; l'icône est décorative (`aria-hidden`).
 */

import { clsx } from 'clsx';
import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

type EmptyTone = 'neutral' | 'tech' | 'grc' | 'success' | 'error';

const TONE_PASTILLE: Record<EmptyTone, string> = {
  neutral: 'border-border bg-surface-hi text-ink-subtle',
  tech: 'border-tech/30 bg-tech-tint/50 text-tech-fg',
  grc: 'border-grc/30 bg-grc-tint/50 text-grc-fg',
  success: 'border-success/30 bg-success-tint/50 text-success',
  error: 'border-urgent/35 bg-urgent-tint/45 text-urgent',
};

interface EmptyStateProps {
  /** Icône lucide affichée dans la pastille. */
  icon: LucideIcon;
  /** Titre court (une ligne). */
  title: string;
  /** Phrase d'explication / d'orientation. Optionnelle. */
  description?: ReactNode;
  /** Bouton(s) d'action — typiquement un <Button> ou deux. */
  action?: ReactNode;
  /** Couleur de la pastille d'icône. Défaut 'neutral'. */
  tone?: EmptyTone;
  /** Variante compacte : moins de padding (pour les sous-sections). */
  compact?: boolean;
  className?: string;
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  tone = 'neutral',
  compact = false,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={clsx(
        'flex flex-col items-center justify-center text-center',
        'rounded-r-2xl border border-border bg-surface/30 backdrop-blur-glass',
        compact ? 'px-4 py-8' : 'px-6 py-14',
        'animate-fade-in',
        className,
      )}
    >
      <div
        aria-hidden="true"
        className={clsx(
          'flex items-center justify-center rounded-full border',
          compact ? 'h-12 w-12' : 'h-16 w-16',
          TONE_PASTILLE[tone],
        )}
      >
        <Icon className={compact ? 'h-5 w-5' : 'h-7 w-7'} strokeWidth={1.75} aria-hidden="true" />
      </div>

      <p
        className={clsx(
          'mt-4 font-display font-semibold tracking-r-snug text-ink',
          compact ? 'text-sm' : 'text-base',
        )}
      >
        {title}
      </p>

      {description && (
        <p className="mt-1.5 max-w-sm text-sm text-ink-muted leading-relaxed">
          {description}
        </p>
      )}

      {action && <div className="mt-5 flex flex-wrap items-center justify-center gap-2">{action}</div>}
    </div>
  );
}
